// Keep the viewer's localization object stable when the application language
// changes. Own the observer and serialize translations so an old locale cannot
// finish translating after a newer one. GenericL10n.translate() owns a separate
// observer whose in-flight work cannot be cancelled; use translateOnce instead.
export class PdfLocalization {
    constructor(GenericL10n, language) {
        this.GenericL10n = GenericL10n;
        this.language = this.locale(language);
        this.current = new GenericL10n(this.language);
        this.roots = new Set();
        this.pending = Promise.resolve();
        this.pendingElements = new Set();
        this.flushQueued = false;
        this.destroyed = false;
        this.pauseDepth = 0;
        this.observer = new MutationObserver(records => this.translateMutations(records));
    }

    locale(language) { return language?.startsWith('fr') ? 'fr' : undefined; }
    enqueue(callback) {
        const result = this.pending.then(() => { if (!this.destroyed) return callback(); });
        this.pending = result.catch(() => {});
        return result;
    }
    elements(root) {
        return [...(root.matches?.('[data-l10n-id]') ? [root] : []), ...root.querySelectorAll('[data-l10n-id]')];
    }
    translateElements(elements) {
        return this.enqueue(() => Promise.all([...new Set(elements)].map(element => this.current.translateOnce(element))));
    }
    observe() {
        if (this.pauseDepth || this.destroyed) return;
        for (const root of this.roots) this.observer.observe(root, {
            childList: true, subtree: true, attributes: true, attributeFilter: ['data-l10n-id', 'data-l10n-args'],
        });
    }
    translateMutations(records) {
        for (const record of records) {
            if (record.type === 'attributes') this.pendingElements.add(record.target);
            else for (const node of record.addedNodes) {
                if (node.nodeType === Node.ELEMENT_NODE) {
                    for (const element of this.elements(node)) this.pendingElements.add(element);
                }
            }
        }
        this.flushMutations();
    }
    flushMutations() {
        if (this.destroyed || this.pauseDepth || this.flushQueued || !this.pendingElements.size) return;
        this.flushQueued = true;
        this.enqueue(() => {
            this.flushQueued = false;
            if (this.pauseDepth) return;
            const elements = [...this.pendingElements].filter(element => element.matches('[data-l10n-id]') &&
                [...this.roots].some(root => root === element || root.contains(element)));
            this.pendingElements.clear();
            return Promise.all(elements.map(element => this.current.translateOnce(element)));
        }).catch(() => {});
    }
    translate(root) {
        if (this.destroyed) return Promise.resolve();
        this.roots.add(root);
        this.observe();
        return this.translateElements(this.elements(root));
    }
    translateOnce(element) { return this.translateElements([element]); }
    setLanguage(language) {
        const locale = this.locale(language);
        return this.enqueue(async () => {
            if (locale === this.language) return;
            await this.current.destroy();
            if (this.destroyed) return;
            this.language = locale;
            this.current = new this.GenericL10n(locale);
            await Promise.all([...this.roots].flatMap(root => this.elements(root)).map(element => this.current.translateOnce(element)));
        });
    }
    getLanguage() { return this.current.getLanguage(); }
    getDirection() { return this.current.getDirection(); }
    get(...args) { return this.enqueue(() => this.current.get(...args)); }
    pause() {
        if (this.pauseDepth++ > 0) return;
        this.translateMutations(this.observer.takeRecords());
        // Keep collecting mutations while PDF.js moves/creates layer nodes.
        // Only translation is paused, so resume needs no whole-document scan.
    }
    resume() {
        if (this.destroyed) return;
        this.pauseDepth = Math.max(0, this.pauseDepth - 1);
        if (this.pauseDepth) return;
        this.translateMutations(this.observer.takeRecords());
    }
    destroy() {
        this.destroyed = true;
        this.observer.disconnect();
        this.roots.clear();
        this.pendingElements.clear();
        return this.pending.then(() => this.current.destroy());
    }
}
